/**
 * D-EZ-110 — SILLY SEASON: the winter, told as a winter of news.
 *
 * Howard: "there's no story season to season." The winter already HAPPENS —
 * `runOffseason` retires men on the age dice, takes licences on SAG/5's risk,
 * re-rates every card, sells broke charters, re-seats the garage and licenses
 * rookies; `runSeasonFinance` signs and drops sponsors and carries every bank.
 * None of that was ever told. This chapter tells it: who walked away and what
 * his career was, who changed shops after how long, who lost his ride, which
 * charters changed hands, whose bank grew and whose emptied, who found speed
 * over the winter and who lost a step, the sponsor news and the rookie class.
 *
 * ⚠ NARRATIVE ONLY (THE GOVERNING RULE, D-EZ-104…111). Every item is a reading
 * of what the winter already decided. Nothing here chooses anything; which
 * sentence tells a fact is a hash of stable ids. The commissioner decides
 * nothing.
 *
 * ⚠ GROUNDED. Every name and number arrives through a slot read off the record
 * (`text.ts`, proved in `chapters.grounding.test.ts`). Real 2025 drivers get
 * their facts and no invented words.
 */

import { dollars } from './text.ts';
import { isRealDriver } from '../story/hometown.ts';
import { winterFacts } from '../paper/winterFacts.ts';
import { seasonAwards } from '../history/index.ts';
import { asHistory, chapterContext, nameIn, seasonTeam, seasonsThrough, teamBase, teamOfIn, tenure, type ChapterContext, type ChapterSeason } from './league.ts';
import { careerRetrospective, listOf } from './farewell.ts';
import { rookieClass, type RookieClass } from './rookies.ts';
import { count, cycle, headlineNamer, ordinal, plural, rotor, tell, tellOne, winterQuote, type Said, type Told } from './text.ts';

export type WinterKind =
  | 'retired'
  | 'moved'
  | 'moved-up'
  | 'dropped'
  | 'licence'
  | 'sold'
  | 'closed'
  | 'bank'
  | 'sponsor'
  | 'aging'
  | 'rookie'
  | 'loyal';

export interface WinterStory {
  kind: WinterKind;
  driverIds: string[];
  /** How big a story it is — what the front page leads with. */
  weight: number;
  /** A short headline, newspaper style. */
  head: Told;
  body: Told[];
  quote: Said | null;
}

export interface WinterSection {
  key: 'walking-away' | 'on-the-move' | 'out-of-a-ride' | 'the-shops' | 'new-colors' | 'the-years' | 'rookies';
  title: string;
  stories: WinterStory[];
  /** A one-line roll call of the smaller items in this section, when there are any. */
  alsoLine: Told | null;
}

export interface SillySeason {
  /** The season just finished; the winter after it. */
  seasonNumber: number;
  nextSeason: number;
  headline: Told;
  dek: Told | null;
  opening: Told[];
  sections: WinterSection[];
  rookieClass: RookieClass | null;
  /** The Hall of Fame class this winter produces (inducted at the start of next season). */
  hallClass: { driverId: string; name: string; citation: string }[];
  counts: {
    retired: number;
    lostRide: number;
    moved: number;
    movedUp: number;
    rookies: number;
    sold: number;
  };
}

/** How many items a section prints in full before the roll call. */
const FULL = { retired: 5, moved: 6, out: 4, years: 4, colors: 4, rookies: 4 };

interface LastYear {
  rank: number;
  wins: number;
  top10: number;
  starts: number;
}

/** The winter after `seasonNumber`, told. Null until that winter has been run. */
export function sillySeason(input: readonly ChapterSeason[], seasonNumber: number, given?: ChapterContext): SillySeason | null {
  const seasons = seasonsThrough(input, seasonNumber, { winter: true });
  const s = seasons.find((x) => x.seasonNumber === seasonNumber);
  const off = s?.offseason;
  if (!s || !off) return null;
  const cx = given ?? chapterContext(seasons, input);
  const used = new Set<string>();
  const K = (...parts: (string | number)[]) => `silly|${cx.leagueKey}|${seasonNumber}|${parts.join('|')}`;
  const standing = s.season.finalStandings.byDriver;
  const nextTeams = off.teams;
  const champId = s.season.championId;
  const headName = headlineNamer([
    ...s.roster.map((c) => [c.driverId, c.name] as [string, string]),
    ...off.roster.map((c) => [c.driverId, c.name] as [string, string]),
  ]);
  const retiredIds = new Set((off.retirements ?? []).map((r) => r.driverId));
  // The headline awards he carries into the winter (champion, Rookie of the Year, Most Improved, Most Popular).
  const honour = new Map<string, string>();
  for (const a of seasonAwards(asHistory(seasons), seasonNumber, cx.registry)) {
    if (a.kind === 'champion' || a.kind === 'rookie-of-the-year' || a.kind === 'most-improved' || a.kind === 'most-popular') if (!honour.has(a.driverId)) honour.set(a.driverId, a.title);
  }
  const last = (id: string): LastYear | null => {
    const st = standing[id];
    return st && st.starts > 0 ? { rank: st.rank, wins: st.wins, top10: st.top10, starts: st.starts } : null;
  };

  // ---- walking away -------------------------------------------------------
  const retH = { champ: rotor(K('ret-h-c')), big: rotor(K('ret-h-b')), small: rotor(K('ret-h-s')) };
  const retB = rotor(K('ret-b'));
  const retY = rotor(K('ret-y'));
  const retStay = rotor(K('ret-stay'));
  const retiredStories: WinterStory[] = [];
  for (const r of off.retirements ?? []) {
    const e = cx.registry.byId[r.driverId];
    const name = e?.name ?? r.name ?? nameIn(seasons, r.driverId);
    const titles = e?.career.championships ?? 0;
    const wins = e?.career.wins ?? 0;
    const leagueSeasons = e?.league.seasons ?? r.seasons ?? 0;
    const champNow = r.driverId === champId;
    const slots = { name, who: headName(r.driverId, name), age: r.age, seasons: count(leagueSeasons), s: plural(leagueSeasons, 'season') };
    const head = champNow
      ? retH.champ(['{who} goes out on top', 'Champion {who} walks away', '{who} retires with the Cup'], slots)
      : titles > 0 || wins >= 5
        ? retH.big(['{who} hangs up the helmet', '{who} calls time on his career', 'End of the road for {who}'], slots)
        : retH.small(['{who} steps away at {age}', '{who} retires', '{who} calls it a career', '{who} walks away'], slots);
    const body: Told[] = [];
    const team = seasonTeam(s, r.driverId);
    const stay = team ? tenure(cx.registry, r.driverId, team.teamId, seasonNumber) : 0;
    const ly = last(r.driverId);
    if (champNow) {
      body.push(tell('{name} won the title and then walked away from it — retired at {age} after {seasons} {s}.', slots));
    } else {
      body.push(
        retB(
          [
            '{name} has retired at {age}, after {seasons} {s} in this league.',
            'At {age}, {name} is done — {seasons} {s} and out.',
            // Reads like his words: never for a real driver.
            ...(isRealDriver(r.driverId) ? [] : ['{name} told the garage over the winter: at {age}, after {seasons} {s}, that’s enough.']),
            '{name} is walking away at {age}. He raced {seasons} {s} here.',
          ],
          slots,
        ),
      );
      if (ly) {
        body.push(
          ly.wins > 0
            ? retY(['He won {w} {wl} in his last year and finished {rank}.', 'He went out a winner — {w} {wl} in his final season, {rank} in the standings.'], { w: count(ly.wins), wl: plural(ly.wins, 'race'), rank: ordinal(ly.rank) })
            : retY(['His last year ended {rank} in the standings.', 'The final season finished {rank} in points.', 'He ended his last year {rank} in the championship.'], { rank: ordinal(ly.rank) }),
        );
      }
    }
    if (team && stay >= 2) body.push(retStay(['He leaves {team} after {n} seasons together.', '{team} loses a man who drove for them {n} seasons running.'], { team: team.name, n: count(stay) }));
    const retro = wins > 0 || leagueSeasons >= 4 ? careerRetrospective(seasons, r.driverId, seasonNumber, cx, { seasonsSaid: true }) : null;
    retiredStories.push({
      kind: 'retired',
      driverIds: [r.driverId],
      weight: 26 + titles * 18 + wins * 3 + (champNow ? 40 : 0) + leagueSeasons + (ly && ly.rank <= 10 ? 10 : 0),
      head,
      body: [...body, ...(retro ? retro.lines.slice(0, 6) : [])],
      quote: winterQuote('retiring', r.driverId, name, cx.people, {}, `${seasonNumber}`, used),
    });
  }
  retiredStories.sort(byWeight);

  // ---- on the move ----------------------------------------------------------
  const mvH = rotor(K('mv-h'));
  const mvB = rotor(K('mv-b'));
  const mvB1 = rotor(K('mv-b1'));
  const upH = rotor(K('up-h'));
  const upB = rotor(K('up-b'));
  const lyR = rotor(K('ly'));
  const drB = rotor(K('dr-b'));
  const moveStories: WinterStory[] = [];
  const dropped: WinterStory[] = [];
  if (nextTeams) {
    for (const card of off.roster) {
      if (!standing[card.driverId] && !s.roster.some((c) => c.driverId === card.driverId)) continue; // a rookie
      const was = seasonTeam(s, card.driverId);
      const now = teamOfIn(nextTeams, card.driverId);
      const e = cx.registry.byId[card.driverId];
      const name = card.name;
      const ly = last(card.driverId);
      const wins = e?.career.wins ?? 0;
      const slots: Record<string, string | number> = { name, who: headName(card.driverId, name), car: card.carNumber };
      if (now) slots.team = now.name;
      if (was) slots.old = was.name;
      const lyWeight = ly ? (ly.rank <= 10 ? 14 : ly.rank <= 20 ? 6 : 0) + ly.wins * 4 : 0;
      if (was && now && teamBase(was.teamId) !== teamBase(now.teamId)) {
        const stay = tenure(cx.registry, card.driverId, was.teamId, seasonNumber);
        slots.n = count(stay);
        const body: Told[] = [
          stay >= 2
            ? mvB(['{name} leaves {old} after {n} seasons for {team}.', 'After {n} seasons at {old}, {name} moves across to {team}.', '{name} is out of {old} after {n} seasons and into the {car} at {team}.'], slots)
            : mvB1(['{name} leaves {old} after a single season for {team}.', 'One year at {old} was enough: {name} moves to {team}.', '{name} trades {old} for {team}, and keeps the {car}.'], slots),
        ];
        if (ly) body.push(lastYearLine(ly, lyR));
        if (honour.has(card.driverId)) body.push(tell('{award} last season.', { award: honour.get(card.driverId) as string }));
        moveStories.push({
          kind: 'moved',
          driverIds: [card.driverId],
          weight: 20 + wins * 2 + lyWeight + (card.driverId === champId ? 40 : 0) + Math.min(stay, 8),
          head: mvH(['{who} to {team}', '{who} jumps to {team}', '{team} lands {who}', '{who} switches to {team}'], slots),
          body,
          quote: winterQuote('moved', card.driverId, name, cx.people, { team: now.name, old: was.name }, `${seasonNumber}`, used),
        });
      } else if (!was && now) {
        const body: Told[] = [
          upB(['{name} ran his own car last year; {team} has put him in the {car}.', 'No more privateer weekends for {name}: {team} signs him into the {car}.', '{team} picks up {name}, who raced without a charter last season.'], slots),
        ];
        if (ly) body.push(lastYearLine(ly, lyR));
        if (honour.has(card.driverId)) body.push(tell('{award} last season.', { award: honour.get(card.driverId) as string }));
        moveStories.push({
          kind: 'moved-up',
          driverIds: [card.driverId],
          weight: 14 + wins * 2 + lyWeight,
          head: upH(['{who} gets his charter ride', '{team} calls {who}', '{who} moves up to {team}'], slots),
          body,
          quote: winterQuote('moved-up', card.driverId, name, cx.people, { team: now.name }, `${seasonNumber}`, used),
        });
      } else if (was && !now) {
        dropped.push({
          kind: 'dropped',
          driverIds: [card.driverId],
          weight: 8 + wins * 2,
          head: tell('{who} loses his seat', slots),
          body: [drB(['{name} is out at {old} and will run his own car this season.', '{old} lets {name} go; he keeps his licence and goes racing on his own.'], slots)],
          quote: null,
        });
      } else if (was && now) {
        const stay = tenure(cx.registry, card.driverId, was.teamId, seasonNumber);
        if (stay >= 5 && (ly?.rank ?? 99) <= 15) {
          moveStories.push({
            kind: 'loyal',
            driverIds: [card.driverId],
            weight: 6 + stay,
            head: tell('{who} stays put', slots),
            body: [tellOne(K('loyal', card.driverId), ['{name} is back at {team} for a {nth} season.', '{name} signs on at {team} again — season number {k} together.'], { ...slots, nth: ordinal(stay + 1), k: stay + 1 })],
            quote: winterQuote('stayed', card.driverId, name, cx.people, { team: now.name }, `${seasonNumber}`, used),
          });
        }
      }
    }
  }
  moveStories.sort(byWeight);
  // Only the longest-serving loyalist makes the paper.
  const loyal = moveStories.filter((m) => m.kind === 'loyal');
  const moves = moveStories.filter((m) => m.kind !== 'loyal' || m === loyal[0]);

  // ---- out of a ride --------------------------------------------------------
  const licR = rotor(K('lic'));
  const outStories: WinterStory[] = [];
  for (const d of off.demotions) {
    if (retiredIds.has(d.driverId)) continue;
    const e = cx.registry.byId[d.driverId];
    const name = e?.name ?? d.name ?? nameIn(seasons, d.driverId);
    const st = standing[d.driverId];
    const was = seasonTeam(s, d.driverId);
    const slots: Record<string, string | number> = { name, who: headName(d.driverId, name) };
    if (st) slots.rank = ordinal(st.rank);
    if (was) slots.team = was.name;
    let line: Told;
    if (d.reason === 'team-closed' && was) line = tell('{name} is out of a ride: {team} shut its doors and nobody bought the charter.', slots);
    else if (d.reason === 'below-finish-threshold' && typeof d.finishPct === 'number') line = tell('{name} loses his licence, having finished only {pct}% of the races he was judged on.', { ...slots, pct: Math.round(d.finishPct * 100) });
    else if (st) line = licR(['{name} loses his licence after finishing {rank}.', 'No licence next year for {name}, {rank} in the standings last season.', 'The licence board takes {name}’s card after a {rank}-place season.'], slots);
    else line = tell('{name} loses his licence.', slots);
    const careerWins = e?.career.wins ?? 0;
    outStories.push({
      kind: 'licence',
      driverIds: [d.driverId],
      weight: 6 + careerWins * 6 + (e?.league.seasons ?? 0),
      head: tell(d.reason === 'team-closed' ? '{who} left without a seat' : '{who} loses his licence', slots),
      body: [line, ...(careerWins > 0 ? [tell('He leaves with {w} career {wl}.', { w: count(careerWins), wl: plural(careerWins, 'win') })] : [])],
      quote: careerWins > 0 || (e?.league.seasons ?? 0) >= 3 ? winterQuote('let-go', d.driverId, name, cx.people, {}, `${seasonNumber}`, used) : null,
    });
  }
  outStories.push(...dropped);
  outStories.sort(byWeight);

  // ---- the shops: charters that changed hands, and the banks -------------
  const shopStories: WinterStory[] = [];
  const soldR = rotor(K('sold'));
  for (const f of off.teamFailures ?? []) {
    const slots: Record<string, string | number> = { old: f.name, red: dollars(Math.abs(Math.min(0, f.balanceMb))) };
    if (f.replacedBy) {
      slots.team = f.replacedBy.name;
      shopStories.push({
        kind: 'sold',
        driverIds: [...f.driverIds],
        weight: 22,
        head: tell('{old} changes hands', slots),
        body: [soldR(['{old} ran out of money — {red} in the red — and the charter has been bought. The cars race as {team} this season.', 'Broke at {red} in the red, {old} has sold its charter. Same cars, new name: {team}.'], slots)],
        quote: null,
      });
    } else {
      shopStories.push({ kind: 'closed', driverIds: [...f.driverIds], weight: 24, head: tell('{old} closes', slots), body: [tell('{old} finished the year {red} in the red and closed its doors. Nobody bought the charter.', slots)], quote: null });
    }
  }
  shopStories.push(...bankStories(seasons, s, K));

  // ---- new colors -----------------------------------------------------------
  const colorStories: WinterStory[] = [];
  const spR = rotor(K('sp'));
  const wf = winterFacts(asHistory(seasons), seasonNumber);
  let staying = 0;
  for (const n of wf?.sponsorNews ?? []) {
    const id = s.season.finalStandings.drivers.find((d) => d.name === n.name)?.driverId;
    if (!id) continue;
    const slots = { name: n.name, who: headName(id, n.name), sponsor: n.sponsor, nth: ordinal(n.seasons) };
    if (n.kind === 'new-primary') {
      colorStories.push({
        kind: 'sponsor',
        driverIds: [id],
        weight: 10,
        head: tell('{sponsor} on {who}’s hood', slots),
        body: [spR(['{name} has a new primary sponsor: {sponsor}.', '{sponsor} signs on as {name}’s primary sponsor this season.', 'New paint for {name}: {sponsor} takes the hood.'], slots)],
        quote: winterQuote('new-sponsor', id, n.name, cx.people, { sponsor: n.sponsor }, `${seasonNumber}`, used),
      });
    } else if (staying < 2 && n.seasons >= 4) {
      staying += 1;
      colorStories.push({ kind: 'sponsor', driverIds: [id], weight: 3, head: tell('{sponsor} stays with {who}', slots), body: [tell('{sponsor} is back on {name}’s car for a {nth} season.', slots)], quote: null });
    }
  }
  colorStories.sort(byWeight);

  // ---- the years: what the age curve did ---------------------------------
  const yearStories: WinterStory[] = [];
  const ageGas = rotor(K('age-gas'));
  const ageEsc = rotor(K('age-esc'));
  const growGas = rotor(K('grow-gas'));
  const growEsc = rotor(K('grow-esc'));
  for (const r of off.rerates ?? []) {
    const a = r.aging;
    if (!a || a.kind === 'none') continue;
    const e = cx.registry.byId[r.driverId];
    const ly = last(r.driverId);
    const wins = e?.career.wins ?? 0;
    const slots = { name: r.name, who: headName(r.driverId, r.name), age: a.age };
    if (a.kind === 'decline') {
      if (wins === 0 && (ly?.rank ?? 99) > 20) continue;
      yearStories.push({
        kind: 'aging',
        driverIds: [r.driverId],
        weight: 7 + wins,
        head: tell('{who} feels the years', slots),
        body: [
          a.gas < 0
            ? ageGas(['{name} turns {age}, and the card shows it: a step slower on the gas pedal.', 'At {age}, {name} has lost a step — one less on the gas pedal.'], slots)
            : ageEsc(['{name} turns {age}; the reflexes are a touch slower out of trouble.', 'At {age}, {name} gets out of trouble a little less often — his escape drops a point.'], slots),
        ],
        quote: null,
      });
    } else {
      if ((ly?.rank ?? 99) > 30) continue;
      yearStories.push({
        kind: 'aging',
        driverIds: [r.driverId],
        weight: 5 + Math.max(0, 30 - (ly?.rank ?? 30)) / 5,
        head: tell('{who} comes back quicker', slots),
        body: [
          a.gas > 0
            ? growGas(['{name}, {age}, found speed over the winter: a point more on the gas pedal.', 'A year older and a year faster: {name} ({age}) gains on the gas pedal.'], slots)
            : growEsc(['{name}, {age}, has learned to stay out of trouble — escape up a point.', '{name} ({age}) comes back steadier: one more point of escape.'], slots),
        ],
        quote: null,
      });
    }
  }
  yearStories.sort(byWeight);

  // ---- the rookie class -----------------------------------------------------
  const rc = rookieClass(input, seasonNumber, cx);
  const rookieStories: WinterStory[] = [];
  const rkH = rotor(K('rk-h'));
  if (rc) {
    for (const r of rc.reports.filter((x) => x.team)) {
      const slots = { who: headName(r.driverId, r.name), car: r.carNumber, team: r.team ?? '' };
      rookieStories.push({
        kind: 'rookie',
        driverIds: [r.driverId],
        weight: 9 + (r.classRank === 1 ? 6 : 0),
        head: rkH(['Rookie {who} lands the {car}', '{team} hands the {car} to rookie {who}', 'Rookie {who} gets a charter ride'], slots),
        body: [r.lead],
        quote: null,
      });
    }
  }

  // ---- the Hall of Fame class this winter produces -----------------------
  const hallClass = (wf?.hallOfFame ?? []).map((h) => ({ driverId: cx.registry.all.find((e) => e.name === h.name)?.driverId ?? h.name, name: h.name, citation: h.citation }));

  const sections: WinterSection[] = [
    section('walking-away', 'Walking away', retiredStories, FULL.retired, K),
    section('on-the-move', 'On the move', moves, FULL.moved, K),
    section('out-of-a-ride', 'Out of a ride', outStories, FULL.out, K),
    section('the-shops', 'The shops', shopStories, 99, K),
    section('new-colors', 'New colors', colorStories, FULL.colors, K),
    section('the-years', 'The years', yearStories, FULL.years, K),
    section('rookies', 'The rookie class', rookieStories, FULL.rookies, K),
  ].filter((x) => x.stories.length > 0);

  const counts = {
    retired: retiredStories.length,
    lostRide: off.demotions.filter((d) => !retiredIds.has(d.driverId)).length,
    moved: moveStories.filter((m) => m.kind === 'moved').length,
    movedUp: moveStories.filter((m) => m.kind === 'moved-up').length,
    rookies: rc?.reports.length ?? 0,
    sold: (off.teamFailures ?? []).length,
  };

  // ---- the front page -------------------------------------------------------
  const all = [...retiredStories, ...moves, ...outStories, ...shopStories, ...colorStories, ...yearStories, ...rookieStories].sort(byWeight);
  const lead = all[0];
  const headline = lead ? lead.head : tell('A quiet winter');
  const second = all.filter((x) => x !== lead).slice(0, 2);
  const dek =
    second.length === 2
      ? tell('Also: {a}; {b}.', { a: second[0].head.text, b: second[1].head.text })
      : second.length === 1
        ? tell('Also: {a}.', { a: second[0].head.text })
        : null;
  const opening: Told[] = [];
  opening.push(
    cycle(`silly-open|${cx.leagueKey}`, seasonNumber, [
      'The haulers were barely unloaded from the finale before the phones started ringing.',
      'Silly season came early this year, and it did not let up until the cars were back on the truck.',
      'The trophy was still being polished when the first rumours went round the garage.',
      'Winter in this sport is never quiet, and this one was no exception.',
      'The garage went dark after the finale. The telephones did not.',
    ], {}),
  );
  const gone = counts.retired + counts.lostRide;
  if (gone > 0) {
    const nSlots = { rides: counts.lostRide === 1 ? 'his ride' : 'their rides', gone: count(gone), d: plural(gone, 'driver'), ret: count(counts.retired), lost: count(counts.lostRide), rookies: count(counts.rookies), r: plural(counts.rookies, 'rookie') };
    opening.push(
      counts.lostRide === 0
        ? tell('The field loses {gone} {d}, all of them to retirement, with {rookies} {r} licensed to fill the gaps.', nSlots)
        : counts.retired === 0
          ? tell('The field loses {gone} {d} to the licence board, with {rookies} {r} licensed to fill the gaps.', nSlots)
          : tellOne(K('open-n'), [
              'The field loses {gone} {d} — {ret} to retirement, {lost} to the licence board — with {rookies} {r} licensed to fill the gaps.',
              'Of last year’s field, {gone} will not be back: {ret} retired and {lost} lost {rides}. In their places, {rookies} {r}.',
            ], nSlots),
    );
  }
  if (counts.moved + counts.movedUp > 0) {
    const mSlots = { moved: count(counts.moved), d: plural(counts.moved, 'driver'), up: count(counts.movedUp), u: plural(counts.movedUp, 'privateer') };
    opening.push(
      counts.moved === 0
        ? tell('Nobody switched shops, but {up} {u} moved up into charter seats.', mSlots)
        : counts.movedUp === 0
          ? tell('In the garage, {moved} {d} changed shops.', mSlots)
          : tell('In the garage, {moved} {d} changed shops and {up} {u} moved up into charter seats.', mSlots),
    );
  }

  return {
    seasonNumber,
    nextSeason: seasonNumber + 1,
    headline,
    dek,
    opening,
    sections,
    rookieClass: rc,
    hallClass,
    counts,
  };
}

function lastYearLine(ly: LastYear, r: ReturnType<typeof rotor>): Told {
  if (ly.wins > 0) return r(['Last season: {rank} in the standings, {w} {wl}.', 'He won {w} {wr} last year and finished {rank}.'], { rank: ordinal(ly.rank), w: count(ly.wins), wl: plural(ly.wins, 'win'), wr: plural(ly.wins, 'race') });
  return r(['Last season: {rank} in the standings, {t} {tt}.', 'He finished {rank} last year, with {t} {tf}.'], { rank: ordinal(ly.rank), t: count(ly.top10), tt: plural(ly.top10, 'top ten'), tf: plural(ly.top10, 'top-ten finish', 'top-ten finishes') });
}

function byWeight(a: WinterStory, b: WinterStory): number {
  return b.weight - a.weight || a.driverIds.join().localeCompare(b.driverIds.join());
}

function section(key: WinterSection['key'], title: string, stories: WinterStory[], full: number, K: (...p: (string | number)[]) => string): WinterSection {
  const shown = stories.slice(0, full);
  const rest = stories.slice(full);
  const alsoLine = rest.length > 0 ? tellOne(K('also', key), ['Also: {list}.', 'And: {list}.'], { list: listOf(rest.map((r) => r.head.text)) }) : null;
  return { key, title, stories: shown, alsoLine };
}

/** The shops' year at the bank: who grew the most and who bled the most. */
function bankStories(seasons: ChapterSeason[], s: ChapterSeason, K: (...p: (string | number)[]) => string): WinterStory[] {
  const now = s.offseason?.carry?.finance.shopBankMb;
  if (!now) return [];
  const prev = seasons.find((x) => x.seasonNumber === s.seasonNumber - 1)?.offseason?.carry?.finance.shopBankMb;
  const rows = s.teams
    .filter((t) => typeof now[t.teamId] === 'number')
    .map((t) => ({ teamId: t.teamId, name: t.name, bank: now[t.teamId], change: prev && typeof prev[t.teamId] === 'number' ? now[t.teamId] - prev[t.teamId] : null }));
  if (rows.length < 3) return [];
  const out: WinterStory[] = [];
  if (prev) {
    const moved = rows.filter((r): r is { teamId: string; name: string; bank: number; change: number } => r.change !== null);
    if (moved.length >= 3) {
      const up = [...moved].sort((a, b) => b.change - a.change || a.teamId.localeCompare(b.teamId))[0];
      const down = [...moved].sort((a, b) => a.change - b.change || a.teamId.localeCompare(b.teamId))[0];
      if (up.change > 0) {
        out.push({
          kind: 'bank',
          driverIds: [],
          weight: 7,
          head: tell('{team} banks the most', { team: up.name }),
          body: [tellOne(K('bank-up', up.teamId), ['{team} put {gain} in the bank over the year — the best year in the garage — and goes into the season with {bank}.', 'Nobody made more money last year than {team}: {gain} added, {bank} in the account.'], { team: up.name, gain: dollars(up.change), bank: dollars(up.bank) })],
          quote: null,
        });
      }
      if (down.change < 0 && down.teamId !== up.teamId) {
        out.push({
          kind: 'bank',
          driverIds: [],
          weight: 8,
          head: tell('Lean times at {team}', { team: down.name }),
          body: [tellOne(K('bank-down', down.teamId), ['{team} lost {loss} over the year, the worst in the garage. The account stands at {bank}.', 'A hard year at the bank for {team}: {loss} gone, {bank} left.'], { team: down.name, loss: dollars(-down.change), bank: dollars(down.bank) })],
          quote: null,
        });
      }
    }
  } else {
    const rich = [...rows].sort((a, b) => b.bank - a.bank || a.teamId.localeCompare(b.teamId))[0];
    out.push({ kind: 'bank', driverIds: [], weight: 6, head: tell('{team} has the deepest pockets', { team: rich.name }), body: [tell('{team} goes into the new season with {bank} in the bank, the most of any shop.', { team: rich.name, bank: dollars(rich.bank) })], quote: null });
  }
  return out;
}
